import React from 'react'

/**
 * COMPONENTE: SOPORTE OVERLAY (VISUAL CHILD)
 * ------------------------------------------------------------------
 * Renderiza la pantalla/banner de soporte técnico (dificultades técnicas).
 * Soporta fondo expandido (fullscreen) o centrado (vertical band).
 * Soporta configuración dinámica de textos, colores, zoom y posicionamiento independiente.
 * ------------------------------------------------------------------
 */
const Soporte = ({ config, windowScale = 1, isAdminPreview = false }) => {
    const {
        titleText,
        titleColor,
        titleSize,
        titleOffsetX,
        titleOffsetY,
        descriptionText,
        descriptionColor,
        descSize,
        descOffsetX,
        descOffsetY,
        accentScale,
        accentOffsetX,
        accentOffsetY,
        visible,
        expanded,
        bgColor,
        scale,
        speed,
        color,
        glow
    } = config;

    if (!visible) return null;

    // Helper para convertir hex a RGB
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result 
            ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
            : '255, 170, 0';
    };

    const neonColorRgb = hexToRgb(color || '#ffaa00');

    // Estilos dinámicos del contenedor
    const containerStyle = {
        '--soporte-zoom': scale ?? 1.0,
        '--soporte-speed': `${speed ?? 1.8}s`,
        '--soporte-neon-color': color ?? '#ffaa00',
        '--soporte-neon-color-rgb': neonColorRgb,
        '--soporte-neon-glow': `${glow ?? 30}px`,
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden'
    };

    // Clases dinámicas del fondo
    const wrapperClasses = `soporte-widget-wrapper ${expanded ? 'expanded' : 'centered-band'}`;

    // Estilo para el fondo personalizado de soporte (se anula el degradado SASS si hay color de fondo personalizado)
    const bgStyle = {
        backgroundColor: bgColor ?? '#151620',
        background: 'none', // Desactiva el radial-gradient de SASS para permitir ver el color seleccionado
        width: expanded ? '100%' : '650px',
        height: '100%',
        position: 'relative'
    };

    return (
        <div className="soporte-display-container" style={containerStyle}>
            <div className={wrapperClasses} style={bgStyle}>
                
                {/* Contenido de soporte con elementos posicionables individualmente */}
                <div className="soporte-content-container" style={{ 
                    width: '1920px', 
                    height: '1080px', 
                    position: 'relative',
                    transform: `scale(${(scale ?? 1.0) * windowScale})`,
                    transformOrigin: 'center center'
                }}>
                    
                    {/* Acento: Ícono ⚠️ y la barra de barrido */}
                    <div 
                        className="soporte-accent-wrapper d-flex flex-column align-items-center gap-3"
                        style={{
                            position: 'absolute',
                            left: `calc(50% + ${accentOffsetX ?? 0}px)`,
                            top: `calc(50% + ${accentOffsetY ?? -220}px)`,
                            transform: `translate(-50%, -50%) scale(${accentScale ?? 1.0})`,
                            transformOrigin: 'center center',
                            transition: 'all 0.2s ease-out',
                            width: '450px'
                        }}
                    >
                        <div className="tech-diff-icon">⚠️</div>
                        <div className="tech-diff-bar-container" style={{ width: '100%' }}>
                            <div className="tech-diff-bar"></div>
                        </div>
                    </div>

                    {/* Título Principal */}
                    <h2 
                        className="tech-diff-title"
                        style={{
                            position: 'absolute',
                            left: `calc(50% + ${titleOffsetX ?? 0}px)`,
                            top: `calc(50% + ${titleOffsetY ?? -80}px)`,
                            transform: 'translate(-50%, -50%)',
                            transformOrigin: 'center center',
                            fontSize: `${titleSize ?? 2.0}rem`,
                            color: titleColor ?? '#ffaa00',
                            fontFamily: "'Orbitron', monospace",
                            fontWeight: 900,
                            letterSpacing: '4px',
                            textShadow: `0 0 calc(var(--soporte-neon-glow, 30px) * 0.4) rgba(var(--soporte-neon-color-rgb, 255, 170, 0), 0.5)`,
                            margin: 0,
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease-out'
                        }}
                    >
                        {titleText}
                    </h2>

                    {/* Mensaje / Descripción */}
                    <p 
                        className="tech-diff-desc"
                        style={{
                            position: 'absolute',
                            left: `calc(50% + ${descOffsetX ?? 0}px)`,
                            top: `calc(50% + ${descOffsetY ?? 50}px)`,
                            transform: 'translate(-50%, -50%)',
                            transformOrigin: 'center center',
                            fontSize: `${descSize ?? 1.0}rem`,
                            color: descriptionColor ?? '#94a3b8',
                            lineHeight: 1.6,
                            maxWidth: '650px',
                            margin: 0,
                            textAlign: 'center',
                            transition: 'all 0.2s ease-out'
                        }}
                    >
                        {descriptionText}
                    </p>

                </div>

            </div>
        </div>
    );
};

export { Soporte };
